import { CircleUser, ExternalLink } from "lucide-react";

import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { useUser } from "../components/auth-provider";

export function SettingsPage() {
	const { user, logout, insertComments, setInsertComments } = useUser();

	enum SettingTabs {
		PROFILE = "Profile",
		SETTINGS = "Settings",
	}

	return (
		<>
			<Tabs className="w-full" defaultValue={SettingTabs.SETTINGS}>
				<TabsList className="w-full">
					<TabsTrigger value={SettingTabs.SETTINGS}>{SettingTabs.SETTINGS}</TabsTrigger>
					<TabsTrigger value={SettingTabs.PROFILE}>{SettingTabs.PROFILE}</TabsTrigger>
				</TabsList>

				<TabsContent value={SettingTabs.SETTINGS}>
					<SettingsContent insertComments={insertComments} setInsertComments={setInsertComments} />
				</TabsContent>
				<TabsContent value={SettingTabs.PROFILE}>
					<UserProfile logout={logout} email={user.email} />
				</TabsContent>
			</Tabs>
		</>
	);
}

function SettingsContent({
	insertComments,
	setInsertComments,
}: {
	insertComments: boolean;
	setInsertComments: React.Dispatch<React.SetStateAction<boolean>>;
}) {
	return (
		<div className="px-2 space-y-4">
			<Card>
				<CardContent className="space-y-4">
					<div className="flex items-center justify-between">
						<div className="space-y-0.5">
							<Label htmlFor="insert-comments" className="text-base font-medium">
								Insert Comments
							</Label>
							<p className="text-sm text-muted-foreground">Automatically insert comments when analyzing documents</p>
						</div>
						<Switch id="insert-comments" checked={insertComments} onCheckedChange={setInsertComments} />
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Getting Started</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<Button
						variant="ghost"
						onClick={() =>
							// TODO: maybe move this to env vars
							window.open(
								"https://pragyaallc-my.sharepoint.com/:b:/g/personal/rajat_legalgraph_ai/ERQvinu0dNZBklTzA5jV4bsBdMrpKQmOqA_zSNcdQlMW_g?e=0TT0Av",
								"_blank"
							)
						}
						className="flex items-center gap-2"
					>
						<ExternalLink size={16} />
						Quick start guide
					</Button>
				</CardContent>
			</Card>
		</div>
	);
}

function UserProfile({ logout, email }: { logout: () => void; email: string }) {
	const versionInfo = [
		{ title: "Version", value: "1.4.5" },
		{ title: "Released", value: "5 Dec, 2025" },
		{ title: "Languages", value: "English" },
	];

	return (
		<div className="px-2">
			<Card>
				<CardContent className="grid gap-3">
					<div className="flex items-center justify-center gap-2">
						<CircleUser size={24} />
						<span className="font-medium">{email}</span>
					</div>
					<Button
						variant="destructive"
						onClick={logout}
						className="bg-red-50 text-red-600 hover:bg-red-100 rounded-md font-medium"
					>
						Log out
					</Button>
				</CardContent>
			</Card>

			<Card className="mt-4 mb-4">
				<CardHeader>
					<CardTitle>Additional Information</CardTitle>
				</CardHeader>
				<CardContent className="grid gap-3">
					<div className="grid gap-2 text-sm">
						{versionInfo.map((item) => (
							<div className="flex justify-between items-center" key={item.title}>
								<span>
									{item.title} {" :"}
								</span>
								<span className="text-[#61607e]">{item.value}</span>
							</div>
						))}
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
